import { positionOf } from './leadPosition';
import { leadStageKey } from './stageKeys';
import type { LeadDealOutcome } from './dealOutcomes';
import type { LeadSource } from './leadFields';
import type { Lead, LeadStage } from './types';

/**
 * The pipeline, one row per origin.
 *
 * ── The question this exists for ───────────────────────────────────────────
 *
 * Kyu, 1 Sep: "how many Inbound Leads (per origin) do we have, who came in
 * recently, have we emailed them, who replied, how many replied, did anyone
 * got promoted to a deal? What's the conversion rate?"
 *
 * A filter shows one origin at a time, so "per origin" meant choosing nine
 * origins in turn and writing the numbers down. That is not a missing column,
 * it is a missing shape.
 *
 * ── FLAT, since 1 Sep. One row per origin, biggest first. ─────────────────
 *
 * It had two levels — a group row with its platforms indented under it — and
 * on fifteen rows of real data that became unreadable. Kyu: "It gives eye
 * stress just trying to figure out which origin is this row and that row...
 * the separation and borders, and how the per origin is laid out etc
 * is...noisy."
 *
 * Three systems were competing to show one hierarchy: the zebra stripe, the
 * indent, and the ink weight. Worse, the stripe cut ACROSS the grouping rather
 * than following it, so a platform row could be striped dark while its own
 * parent sat light. None of them won and the reader lost the row.
 *
 * So there is no hierarchy left to decode. Every row is one origin, the thing
 * money is actually spent on — which is also what Kyu asked for when he said
 * "pls dont use 'Advertising' only label. We gotta be specific. LinkedIn Ads,
 * FB Ads, etc."
 *
 * The group subtotals are gone with it, and two things make that survivable:
 * the direction tabs already answer "how is outbound doing" for the only
 * grouping anybody asked about, and a group named "Outbound" reading 201 on a
 * screen whose Outbound TAB read 323 was actively confusing.
 *
 * ── The window is a COHORT, and that is a deliberate choice ───────────────
 *
 * Callers pass leads already narrowed to a date range. The range means WHEN
 * THE LEAD ARRIVED, and every other column is then "ever, for those leads".
 *
 * The alternative — count emails sent in the window, replies received in the
 * window — mixes cohorts and makes the conversion rate meaningless: replies
 * from leads that arrived last year, over leads that arrived this week.
 *
 * The column headings already tell the cohort story — They arrived / We
 * reached out / What came of it is one group of people moving through stages —
 * so this is the reading the table was already shaped for.
 *
 * ── The row nothing else shows ─────────────────────────────────────────────
 *
 * "No origin recorded" is a row here. Every other view drops leads with no
 * origin, and a cohort that quietly excludes the untagged reports a conversion
 * rate off a denominator missing leads. It is also the row that says when
 * tagging has slipped.
 */

export interface OriginRow {
  /** Stable key for React and for the click that filters by this row. */
  key: string;
  label: string;
  /** The group this row filters to, or null for the untagged row. */
  group: string | null;
  /** The platform this row filters to. Null on the untagged row. */
  originId: string | null;
  arrived: number;
  emailed: number;
  replied: number;
  /**
   * Ever reached a deal — open, won or lost. Read off the deals themselves, so
   * a lead with a win behind it and a live deal now still counts here.
   */
  deals: number;
  /**
   * WON deals over everyone who arrived, one decimal. 0 rather than NaN on an
   * empty row.
   *
   * ── Why this counts wins and not deals ───────────────────────────────────
   *
   * It was deals / arrived until 1 Sep, and that made the table print 42.9%
   * beside LinkedIn Ads — a channel with three open deals, nothing won and no
   * revenue. Kyu, reading it: "what is 42.9% to you? conversion rate?"
   *
   * It was not. Three deals out of seven leads is a lead-to-opportunity rate.
   * "Conversion" unqualified means lead to customer, and a number sitting
   * under that word while the channel has closed nothing is the screen
   * misleading whoever reads it.
   *
   * His call, and it is the simpler one: "an inbound lead that becomes a deal
   * is very common. We should just use 1 conversion - if it gets WON."
   *
   * A lost deal counts against this, correctly — the lead arrived, was worked,
   * and did not buy. That is precisely a lead that failed to convert, and a
   * rate that dropped losses would flatter every channel.
   *
   * WIDENED 2 Sep to "ever won" rather than "currently reads as won". A client
   * who bought and has since opened another deal was counting as a
   * non-conversion, which punished the best outcome the business has. See the
   * block above the tally loop.
   */
  conversion: number;
  won: number;
  lost: number;
  closed: number;
  /**
   * Lost plus Closed — the leads that are not coming back.
   *
   * To somebody reading across a row they say one thing. The distinction is
   * not lost: the two bands below are still separate, and a closed lead still
   * carries its reason on its own row, which is where "not interested, fell
   * off, no fit" is actually legible.
   */
  gone: number;
  /** Arrived and never emailed. The one figure that earns the accent. */
  neverEmailed: number;
}

const UNTAGGED = 'no-origin';

interface Tally {
  key: string;
  label: string;
  group: string | null;
  originId: string | null;
  arrived: number;
  emailed: number;
  replied: number;
  deals: number;
  won: number;
  lost: number;
  closed: number;
  neverEmailed: number;
}

const blank = (
  key: string,
  label: string,
  group: string | null,
  originId: string | null,
): Tally => ({
  key,
  label,
  group,
  originId,
  arrived: 0,
  emailed: 0,
  replied: 0,
  deals: 0,
  won: 0,
  lost: 0,
  closed: 0,
  neverEmailed: 0,
});

/** won / arrived as a percentage to one decimal. 0 rather than NaN on nobody. */
function rate(won: number, arrived: number): number {
  return arrived === 0 ? 0 : Math.round((won / arrived) * 1000) / 10;
}

function finish(tally: Tally): OriginRow {
  return {
    ...tally,
    gone: tally.lost + tally.closed,
    conversion: rate(tally.won, tally.arrived),
  };
}

/**
 * The total across the whole slice, as a row of the same shape.
 *
 * ── Why this is a row and not five tiles ───────────────────────────────────
 *
 * Until 1 Sep these numbers sat above the table in their own grid, under the
 * heading "What we have done". They were never a different measurement — they
 * are this table's total, lifted out. Reading one origin's 48 against the 90
 * for everybody was a memory test across two grids; in a total row under the
 * column it is one eye movement.
 *
 * The conversion rate is recomputed from the summed WINS and arrived, never
 * averaged from the per-row rates: the mean of nine percentages weights a
 * nine-lead origin the same as a four-hundred-lead one and quietly reports a
 * number that is true of nothing.
 */
export function originTotal(
  rows: readonly OriginRow[],
  label = 'Everyone in this slice',
): OriginRow {
  const total = rows.reduce<Omit<OriginRow, 'conversion'>>(
    (sum, row) => ({
      ...sum,
      arrived: sum.arrived + row.arrived,
      emailed: sum.emailed + row.emailed,
      replied: sum.replied + row.replied,
      deals: sum.deals + row.deals,
      won: sum.won + row.won,
      lost: sum.lost + row.lost,
      closed: sum.closed + row.closed,
      gone: sum.gone + row.gone,
      neverEmailed: sum.neverEmailed + row.neverEmailed,
    }),
    {
      key: 'total',
      label,
      group: null,
      originId: null,
      arrived: 0,
      emailed: 0,
      replied: 0,
      deals: 0,
      won: 0,
      lost: 0,
      closed: 0,
      gone: 0,
      neverEmailed: 0,
    },
  );
  return { ...total, conversion: rate(total.won, total.arrived) };
}

export function originBreakdown(
  leads: readonly Lead[],
  origins: readonly LeadSource[],
  stages: readonly LeadStage[],
  outcomes: ReadonlyMap<string, LeadDealOutcome>,
  repliedIds: ReadonlySet<string>,
): OriginRow[] {
  const source = new Map(origins.map((entry) => [String(entry.id), entry]));
  const byOrigin = new Map<string, Tally>();
  const untagged = blank(UNTAGGED, 'No origin recorded', null, null);

  for (const lead of leads) {
    const hasReplied = repliedIds.has(lead.id);
    // A reply counts as contact whatever the touch counter says: somebody who
    // answered was plainly reached, and the outreach may have happened in
    // Gmail with only the answer logged here.
    const emailed = lead.touch_count > 0 || hasReplied;
    const position = positionOf(leadStageKey(lead.stage_id, [...stages]), outcomes.get(lead.id));

    /*
     * ── Outcome columns are read off the DEALS, not off the band ───────────
     *
     * REWRITTEN 2 Sep. These four used to be derived from `position`, which is
     * where a lead SITS — one label, with open work outranking history so a
     * returning customer reads "in talks" rather than "won".
     *
     * That rule is right for the bands and wrong here. Kyu, 2 Sep: "we did
     * have won deals over the past 6 months aside from Lazzara... all won
     * deals, according to the timeline, should reflect to conversion
     * accordingly."
     *
     * He is right, and the old code failed in a way that flattered nothing and
     * hid the best outcome there is. Measured: a lead with one WON deal and
     * one OPEN deal resolved to `engaged`, so it counted in NONE of these — not
     * Deals, not Won, not Lost. A client who bought and came back was invisible
     * in every outcome column, and the conversion rate treated repeat business
     * as a failure to convert.
     *
     * So: "where is this lead now" and "what has this lead ever done" are two
     * questions, and only the first one is a position. A win is a fact that
     * happened; another deal opening does not un-happen it.
     *
     * `closed` stays with the position, because closed is a human decision with
     * a typed reason attached rather than something the deals decide.
     */
    const outcome = outcomes.get(lead.id);
    const wonDeals = outcome?.won_deals ?? 0;
    const lostDeals = outcome?.lost_deals ?? 0;
    const openDeals = outcome?.open_deals ?? 0;
    const everWon = wonDeals > 0;
    // "Ever reached a deal" OR the team said so on the lead. The second half
    // keeps the stuck case leadPosition.ts names: somebody can set a lead to
    // Converted to Deal from the dropdown without raising one, and those leads
    // have counted in this column since it was built. Dropping them here would
    // quietly lower a number Kyu reads without telling him why — the tagging
    // gap is real, but this column is not the place to punish it.
    const everDeal = wonDeals + lostDeals + openDeals > 0 || position === 'converted';
    // Lost means DONE and did not buy: no win behind them and nothing still
    // running. A lead with a loss and a live deal is still in play, and one
    // with a loss and a win is a customer.
    const isLost = lostDeals > 0 && wonDeals === 0 && openDeals === 0;
    // A lead somebody closed who nonetheless bought is not "gone". Without
    // this guard a returning customer whose lead was later closed would be
    // counted both as a win and as lost-to-us in the same row.
    const isClosed = position === 'closed' && !everWon;

    const apply = (tally: Tally) => {
      tally.arrived += 1;
      if (emailed) tally.emailed += 1;
      else tally.neverEmailed += 1;
      if (hasReplied) tally.replied += 1;
      if (everDeal) tally.deals += 1;
      if (everWon) tally.won += 1;
      if (isLost) tally.lost += 1;
      if (isClosed) tally.closed += 1;
    };

    // Two separate guards rather than one ternary, so the compiler can see
    // that `originId` is a string wherever `entry` is defined.
    if (lead.lead_source_id === null) {
      apply(untagged);
      continue;
    }
    const originId = String(lead.lead_source_id);
    const entry = source.get(originId);
    if (entry === undefined) {
      // A lead pointing at an origin the vocabulary no longer holds. Counting
      // it as untagged is honest; dropping it would make the rows stop summing
      // to the slice.
      apply(untagged);
      continue;
    }

    let row = byOrigin.get(originId);
    if (row === undefined) {
      // The group is kept on the row — not to draw, but because clicking a row
      // sets the same filter the Lead Origin dropdown sets, and that control
      // is addressed by group AND platform.
      const groupName = (entry.group_name ?? '').trim() || String(entry.name);
      row = blank(`origin:${originId}`, String(entry.name), groupName, originId);
      byOrigin.set(originId, row);
    }
    apply(row);
  }

  // Biggest first. The first question anybody asks a table like this is "where
  // is most of it coming from", and alphabetical buries that.
  const rows = [...byOrigin.values()]
    .sort((a, b) => b.arrived - a.arrived || a.label.localeCompare(b.label))
    .map(finish);

  if (untagged.arrived > 0) {
    // Last, always — it is a gap in the data rather than a channel, and
    // sorting it by size would put it above real origins on a bad week.
    rows.push(finish(untagged));
  }
  return rows;
}
